/**
 * OM_Simple — the CLN colour-flip rule, in one file with NO IMPORTS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE
 *   The CLN candle colour is decided by the linear regression, never by the real
 *   candle's own direction:  `up = linreg(close, n)[i] >= linreg(open, n)[i]`.
 *   red -> green  =>  BUY
 *   green -> red  =>  SHORT
 *   There is no Sell and no Cover. A BUY is closed only by the next SHORT, so
 *   this is an always-in-the-market stop-and-reverse system with NO STOP of its
 *   own. That is deliberate and it is the owner's decision: OM_Simple is a
 *   SIGNAL generator, confirmed by eye, with the stop and target placed by hand
 *   on the chart afterwards. Auto-trade is refused by omSimpleScan.service.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE HAS NO IMPORTS
 *   It is a BYTE-IDENTICAL TWIN, the same arrangement dailyBlastEngine.ts has:
 *       frontend/src/utils/strategies/omSimpleEngine.ts     <- source of truth
 *       backend/src/services/strategies/omSimpleEngine.ts
 *   guarded by `node scripts/check-engine-parity.mjs`. The owner's requirement
 *   is that a scan signal always lands on the same bar the chart marks, so the
 *   two sides must not merely agree — they must be the same bytes. Taking the
 *   CLN bars as an ARGUMENT rather than importing `computeClnBars` (whose path
 *   differs on the two sides) is what makes that possible.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO THINGS THAT LOOK LIKE DETAILS AND ARE NOT
 *
 * 1. WARM-UP. `linreg` fits over `n = min(period, i+1)`, so a colour exists from
 *    bar 0 — but it is fitted on a partial window and is not the colour the user
 *    will see once the series is long. The first index whose own window AND its
 *    predecessor's are both full is `clnLength`, so nothing is emitted before it.
 *
 * 2. ACTED COLOUR, not previous bar's colour. The flip test compares against the
 *    last colour that actually EMITTED, not against `cln[i-1].up`. With no
 *    filter the two are identical; with `suppressVolContradiction` on they are
 *    not, and comparing to `i-1` would silently swallow the whole leg — the
 *    suppressed bar would become the new reference and the eventual reversal
 *    would never be seen as a flip.
 *
 * CAUSALITY. `linreg` at bar i reads only bars <= i, so a closed bar's colour
 * never moves once it is closed. The chart paints the FORMING bar's colour live
 * and that one does move; callers must therefore pass CLOSED bars only (the
 * chart overlay reads `paneData`, the scan uses `dropFormingBar`).
 */

/** The regression length OM_Simple evaluates unless told otherwise.
 *
 *  NOT the chart's `CLN_DEFAULT_LENGTH` of 14, and the difference is measured,
 *  not stylistic. Over 10 majors, 2024-03 -> 2026-09 at 7H with 0.20% round-trip
 *  costs, this rule at length 14 fired 22.5 times per month per symbol and
 *  returned -0.125%/trade (PF 0.91, positive on 1 of 10 symbols); at length 50
 *  it fired 11.3 times and returned +0.045% (PF 1.03, positive on 7 of 10).
 *  14 loses to costs. 50 is the shipped default and is roughly break-even —
 *  which is why this is a signal generator and not an autonomous edge. */
export const OM_SIMPLE_DEFAULT_CLN_LENGTH = 50;

export type OmSimpleAction = 'BUY' | 'SHORT';
export type OmSimplePos = 'LONG' | 'SHORT' | 'FLAT';
/** Reason codes — mapped to plain English in signalLabels.ts. */
export type OmSimpleReason = 'OMS_FLIP_UP' | 'OMS_FLIP_DOWN';

/** The only fields of a CLN bar the rule reads. `ClnBar` on both sides is a
 *  structural superset of this, so either side's array can be passed directly. */
export interface OmSimpleClnBar {
  time: number;
  up: boolean;
  volContradiction?: boolean;
}

export interface OmSimpleOptions {
  /** Regression length the CLN bars were computed at. Used only for the warm-up
   *  floor — the colours themselves are already decided by the caller. */
  clnLength?: number;
  /** Skip a flip printed on thin participation (`volContradiction`).
   *
   *  Ships OFF. It measurably helps expectancy (4H/cln50 over 48 symbols,
   *  2020-2026: +0.095% -> +0.165%, PF 1.05 -> 1.08) but costs breadth
   *  (32/48 -> 31/48), and more importantly it would let the chart show a colour
   *  change with no marker against it — which breaks the one property this
   *  strategy exists to have. */
  suppressVolContradiction?: boolean;
}

export interface OmSimpleStep {
  time: number;
  /** The bar's CLN colour: true = green. */
  up: boolean;
  /** Fresh signal on this bar, or null. Never an exit — there are none. */
  signal: OmSimpleAction | null;
  reason: OmSimpleReason | null;
  /** Running position AFTER this bar's signal is applied. */
  pos: OmSimplePos;
  /** True when the flip was seen but suppressed by a filter. Reported so a scan
   *  can say "raw SHORT suppressed" instead of the uninformative "no signal". */
  suppressed: boolean;
}

/**
 * Walk the CLN colours and emit one signal per colour change.
 *
 * `cln` must be ascending, CLOSED bars only, and computed at `clnLength`.
 * Returns one step per input bar so callers can index by bar.
 */
export function computeOmSimpleSteps(
  cln: ReadonlyArray<OmSimpleClnBar>,
  opts: OmSimpleOptions = {},
): OmSimpleStep[] {
  const steps: OmSimpleStep[] = [];
  const n = cln ? cln.length : 0;
  if (!n) return steps;

  const rawLen = Number(opts.clnLength);
  const clnLength = Number.isFinite(rawLen) && rawLen >= 2
    ? Math.round(rawLen)
    : OM_SIMPLE_DEFAULT_CLN_LENGTH;
  const suppress = opts.suppressVolContradiction === true;

  // First index whose own regression window AND its predecessor's are both full.
  const warm = clnLength;

  let pos: OmSimplePos = 'FLAT';
  let acted: boolean | null = null;

  for (let i = 0; i < n; i++) {
    const bar = cln[i];
    let signal: OmSimpleAction | null = null;
    let reason: OmSimpleReason | null = null;
    let suppressed = false;

    if (i >= warm) {
      // The colour standing when the watch begins is the reference, so the first
      // flip AFTER warm-up emits rather than being swallowed as "the first bar".
      if (acted === null) acted = cln[i - 1].up;

      if (bar.up !== acted) {
        if (suppress && bar.volContradiction === true) {
          suppressed = true;
        } else {
          signal = bar.up ? 'BUY' : 'SHORT';
          reason = bar.up ? 'OMS_FLIP_UP' : 'OMS_FLIP_DOWN';
          pos = bar.up ? 'LONG' : 'SHORT';
          acted = bar.up;
        }
      }
    }

    steps.push({ time: bar.time, up: bar.up, signal, reason, pos, suppressed });
  }

  return steps;
}
